import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { styled } from 'styled-components';

import { t } from 'translations';
import { Button, CTALoader, Form, Loader, RadioGroup, RadioGroupOption, SubmitButton } from 'ui';
import { SmallText } from 'ui/styles/text';
import { media } from 'ui/themes/signup';

import { useCreatePaymentSignature } from '../../hooks/useCreatePaymentSignature';
import { Currency, FlowType, HostedPageParams, PaymentSignature, PrepopulateFields } from '../../types';
import { throwError } from '../../utils/errorHandler';
import { addEventHandler, ClientErrorMessageCallback, submitForm } from '../../utils/zuora';
import { TermsDialog } from '../TermsDialog';
import CreditCardLogo from './assets/credit.svg?react';
import PaypalLogo from './assets/paypal.svg?react';
import { CreditCardForm } from './CreditCardForm';
import { HSAEligibilityBanner } from './HSAEligibilityBanner';
import { PayPalForm, PayPalFormProps } from './PayPalForm';

const PAGE_URL = import.meta.env.VITE_ZUORA_PAGE_URL;

type PaymentMethod = 'card' | 'hsa' | 'paypal';

const PaymentForm = styled(Form)`
  align-items: center;
  gap: 25px;

  & > div {
    width: 100%;
  }

  /* Overrides default form behaviour */
  ${Button}[type="submit"] {
    align-self: center;
    margin: 0;
  }

  ${media.xlarge`
    align-items: flex-start;
  `}
`;

const MethodContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
`;

const LabelText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface CreditCardInputs {
  currency: Currency;
  pageId: string;
  gatewayName: string;
  prepopulatedFields: PrepopulateFields;
  onSubmit: React.MouseEventHandler<HTMLButtonElement>;
  clientErrorCallback: ClientErrorMessageCallback;
}

interface PaymentMethodFormProps {
  creditCard: CreditCardInputs;
  hsa: CreditCardInputs;
  paypal: {
    gatewayName?: string;
    componentId: PayPalFormProps['id'];
    merchantId: string;
    onSubmit: PayPalFormProps['onStart'];
  };
  flowType: FlowType;
  hsaEligible: boolean;
}

type ZuoraHostedPageFormProps = {
  flowType: FlowType;
  paymentSignature: PaymentSignature | undefined;
} & CreditCardInputs;

const ZuoraHostedPageForm = ({
  clientErrorCallback,
  currency,
  flowType,
  gatewayName,
  onSubmit,
  pageId,
  paymentSignature,
  prepopulatedFields,
}: ZuoraHostedPageFormProps) => {
  const intl = useIntl();
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Use Zuora event handler for any side-effects
    addEventHandler('onSubmit', () => {
      setDisableSubmit(true);
    });

    addEventHandler('onloadCallback', () => {
      setDisableSubmit(false);
      setIsLoading(false);
    });
  }, []);

  const hostedPageParams: HostedPageParams | undefined = paymentSignature && {
    ...paymentSignature,
    id: pageId,
    paymentGateway: gatewayName,
    style: 'inline',
    submitEnabled: false,
    locale: intl.locale,
    url: PAGE_URL,
    field_currency: currency,
    field_passthrough1: gatewayName,
    field_passthrough2: flowType,
  };

  return (
    <>
      {/* Render a loader while CreditCardForm component is loading. onloadCallback will change isLoading state */}
      {isLoading && <Loader isWholePage={false} />}

      {/* Need to render the form, otherwise onloadCallback will never trigger. This is an empty div when Zuora is loading */}
      {hostedPageParams && (
        <CreditCardForm
          hostedPageParams={hostedPageParams}
          prepopulatedFields={prepopulatedFields}
          clientErrorCallback={clientErrorCallback}
        />
      )}

      {!isLoading && (
        <SubmitButton
          disabled={disableSubmit}
          onClick={(e) => {
            e.preventDefault();
            onSubmit(e);
            submitForm();
          }}
          data-testid="credit-card-submit-button"
        >
          {disableSubmit ? <CTALoader /> : t('membership_signup_payment_method_button_submit')}
        </SubmitButton>
      )}
    </>
  );
};

export const PaymentMethodForm = ({ flowType, creditCard, hsa, paypal, hsaEligible }: PaymentMethodFormProps) => {
  const { register, watch } = useForm<{
    paymentMethod: PaymentMethod;
  }>({
    defaultValues: {
      paymentMethod: undefined,
    },
  });
  const paymentMethod = watch('paymentMethod');

  const { data: paymentSignature, error, mutate: getSignature } = useCreatePaymentSignature();

  // On payment method selection change
  useEffect(() => {
    // creditCard.pageId can be an empty string until gateways are fetched
    if (paymentMethod === 'card' && creditCard.pageId) {
      getSignature({ pageId: creditCard.pageId });
    }

    if (paymentMethod === 'hsa' && hsa.pageId) {
      getSignature({ pageId: hsa.pageId });
    }
  }, [creditCard.pageId, hsa.pageId, getSignature, paymentMethod]);

  if (error) {
    throwError('failedPaymentSignatureCreation', error);
  }

  return (
    <PaymentForm
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <RadioGroup>
        <RadioGroupOption
          {...register('paymentMethod')}
          data-testid="credit-card-selector-button"
          value="card"
          labelText={
            <LabelText>
              <CreditCardLogo height="24px" />
              <SmallText $fontWeight="bold" $color="white">
                {t('membership_hub_credit_card')}
              </SmallText>
            </LabelText>
          }
        >
          {paymentMethod === 'card' && (
            <MethodContainer>
              <ZuoraHostedPageForm
                key={paymentSignature?.key} // Force re-render when signarture changes
                clientErrorCallback={creditCard.clientErrorCallback}
                currency={creditCard.currency}
                flowType={flowType}
                gatewayName={creditCard.gatewayName}
                onSubmit={creditCard.onSubmit}
                pageId={creditCard.pageId}
                paymentSignature={paymentSignature}
                prepopulatedFields={creditCard.prepopulatedFields}
              />
            </MethodContainer>
          )}
        </RadioGroupOption>

        {paypal.gatewayName && (
          <RadioGroupOption
            {...register('paymentMethod')}
            data-testid="paypal-selector-button"
            value="paypal"
            labelText={
              <LabelText>
                <PaypalLogo height="24px" />
                <SmallText $fontWeight="bold" $color="white">
                  {t('membership_hub_paypal')}
                </SmallText>
              </LabelText>
            }
          >
            {paymentMethod === 'paypal' && (
              <MethodContainer>
                <PayPalForm id={paypal.componentId} onStart={paypal.onSubmit} paypalMerchantId={paypal.merchantId} />
              </MethodContainer>
            )}
          </RadioGroupOption>
        )}

        {hsaEligible && (
          <RadioGroupOption
            {...register('paymentMethod')}
            data-testid="hsa-selector-button"
            value="hsa"
            labelText={
              <LabelText>
                <CreditCardLogo height="24px" />
                <SmallText $fontWeight="bold" $color="white">
                  HSA/FSA
                </SmallText>
              </LabelText>
            }
          >
            {paymentMethod === 'hsa' && (
              <MethodContainer>
                <ZuoraHostedPageForm
                  key={paymentSignature?.key} // Force re-render when signarture changes
                  clientErrorCallback={hsa.clientErrorCallback}
                  currency={hsa.currency}
                  flowType={flowType}
                  gatewayName={hsa.gatewayName}
                  onSubmit={hsa.onSubmit}
                  pageId={hsa.pageId}
                  paymentSignature={paymentSignature}
                  prepopulatedFields={hsa.prepopulatedFields}
                />
              </MethodContainer>
            )}
          </RadioGroupOption>
        )}
      </RadioGroup>
      {hsaEligible && <HSAEligibilityBanner />}
      <TermsDialog simpleTerms={true} />
    </PaymentForm>
  );
};
